// Kits (tienda v5) — port de maquetas/tienda-v5-kits.html.
// Shared: TopNav (kits), Breadcrumb, Footer, BottomNav, MiniBar, CrossLinkBanner (gold).
// Page-specific scopeado bajo .tv5-page-kits. Datos LIVE desde la DB (RPC
// cliente_get_kits_con_productos, misma fuente que la Auditoría de kits) vía kitViewModel.
// Cualquier cambio en /admin/kits se refleja acá en vivo (2026-07-24: antes leía el archivo
// hardcodeado src/data/kits.js → productos desincronizados).
// Preserva: hero + precio ancla, franja con containment, tabs filtro, cards con
// "Ver los N productos" colapsable (mobile) + card clickeable → /kit/:codigo.
//
// Nota: el precio de cada card es el compacto de la maqueta (no el PrecioBox shared, que
// es el bloque grande de las páginas de detalle — usarlo acá cambiaría el look de la card).
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TopNav, Footer, BottomNav, MiniBar, Breadcrumb, CrossLinkBanner, ScrollHintPill } from '../../components/tienda-v5';
import { TV5_ROUTES } from '../../components/tienda-v5/routes';
import { fetchKitsConProductos } from '../../lib/tiendaApi';
import { kitViewModel, ordenarKitsView } from '../../lib/kitsView';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

const TABS = [
  { key: 'todos', label: 'Todos' },
  { key: 'essential', label: 'Essential' },
  { key: 'premium', label: 'Premium' },
];

function KitCard({ k }) {
  const [expandida, setExpandida] = useState(false);
  const nav = useNavigate();
  const n = k.productos.length;

  function onCardClick(e) {
    if (e.target.closest('a, button')) return;
    nav('/kit/' + k.codigo);
  }

  return (
    <div
      className={'card-kit' + (k.destacado ? ' destacado' : '') + (k.premium ? ' premium' : '')}
      onClick={onCardClick}
    >
      {k.destacado && <div className="badge-mas-pedido">El más pedido</div>}
      <div className="card-kit-tier">{k.premium ? '★ Premium' : k.tierLabel}</div>
      <div className="card-kit-nombre">{k.nombre}</div>
      <div className="card-kit-para"><b>{k.personas}</b> · {k.productos.length} productos</div>

      <div className={'card-kit-productos-lista' + (expandida ? ' expandida' : '')}>
        {k.productos.map((p, i) => (
          <div key={i}><span className="cant">{p.cant}</span><strong>{p.nombre}</strong> {p.det}</div>
        ))}
      </div>
      <button
        type="button"
        className={'card-kit-ver-todos' + (expandida ? ' expandida' : '')}
        onClick={(e) => { e.stopPropagation(); setExpandida((v) => !v); }}
      >
        {expandida ? 'Ver menos ' : `Ver los ${n} productos `}<span className="vt-arrow">▾</span>
      </button>

      <div className="card-kit-precio-row">
        <div className="card-kit-precio">{fmt(k.precio)}</div>
        <div className="card-kit-por-mes">/ mes</div>
      </div>
      {k.ahorro > 0 && (
        <div className="card-kit-ahorro-linea">
          <span className="card-kit-ahorro-num">{fmt(k.ahorro)} menos</span> que en el súper
          <span className="card-kit-ahorro-tach">{fmt(k.tachado)}</span>
        </div>
      )}
      <Link to={'/kit/' + k.codigo} className="card-kit-cta">Descubrí el {k.nombre} →</Link>
    </div>
  );
}

export default function Kits() {
  const [filtro, setFiltro] = useState('todos');
  const [kits, setKits] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    fetchKitsConProductos()
      .then((map) => {
        if (!vivo) return;
        const vms = ordenarKitsView(Object.values(map).map(kitViewModel));
        setKits(vms);
      })
      .catch((e) => { if (vivo) console.warn('[Kits] fetch kits falló:', e?.message || e); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  const visibles = kits.filter((k) => filtro === 'todos' || k.grupo === filtro);

  return (
    <div className="tv5-page-kits">
      <style>{KITS_CSS}</style>
      <TopNav active="kits" accent="teal" />
      <Breadcrumb backHref={TV5_ROUTES.home} backLabel="Home" current="Kits mensuales" />

      <main>
        <section className="hero">
          <h1>Elegí el tuyo. Cambiás cuando quieras.</h1>
          <p>Seis kits pensados para distintos tipos de casa. Uno rinde para un mes entero.</p>
          <div className="hero-precio-ancla">Desde <strong>$990</strong>/mes · envío sin costo</div>
        </section>

        <div className="franja-envio">🚚 <span>Pasamos por tu barrio · <strong>Envío sin costo</strong> en cada entrega</span></div>

        <div className="tabs-segmentos-wrap">
          <div className="tabs-segmentos">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={'tab-segmento' + (filtro === t.key ? ' activo' : '')}
                onClick={() => setFiltro(t.key)}
              >{t.label}</button>
            ))}
          </div>
        </div>

        <section className="grid-kits">
          {cargando
            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="card-kit-skeleton" />)
            : visibles.map((k) => <KitCard key={k.codigo} k={k} />)}
        </section>
        {!cargando && visibles.length === 0 && (
          <p className="kits-vacio">No hay kits para mostrar en este momento.</p>
        )}

        <CrossLinkBanner
          accent="gold"
          title="¿Preferís sin compromiso?"
          sub="Comprá suelto lo que te falte, cuando quieras. 85+ productos elegidos uno por uno."
          ctaLabel="Ir a la Tienda →"
          ctaHref={TV5_ROUTES.tienda}
        />
      </main>

      <ScrollHintPill />
      <MiniBar checkoutHref={TV5_ROUTES.checkout} />
      <Footer accent="teal" />
      <BottomNav active="kits" />
    </div>
  );
}

const KITS_CSS = `
.tv5-page-kits {
  --tv5k-navy:#1A3461; --tv5k-teal:#00A8B5; --tv5k-gold:#C5A059; --tv5k-green:#10B981;
  --tv5k-bg:#0A1424; --tv5k-card:rgba(255,255,255,0.04); --tv5k-border:rgba(255,255,255,0.08);
  --tv5k-text:#F8F6F1; --tv5k-muted:rgba(248,246,241,0.65); --tv5k-dim:rgba(248,246,241,0.45);
  --safe-bottom:env(safe-area-inset-bottom,0px);
  background:var(--tv5k-bg); color:var(--tv5k-text); min-height:100vh; position:relative;
  overflow-x:clip; padding-bottom:calc(72px + var(--safe-bottom));
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
.tv5-page-kits::before { content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
  background:radial-gradient(circle at 20% 10%, rgba(0,168,181,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 60%, rgba(197,160,89,0.08) 0%, transparent 40%); }
.tv5-page-kits main { position:relative; z-index:1; }
@media (min-width:900px){ .tv5-page-kits { padding-bottom:0; } }

.tv5-page-kits .hero { padding:20px 20px 8px; text-align:center; max-width:900px; margin:0 auto; }
.tv5-page-kits .hero h1 { font-size:clamp(26px,6vw,44px); font-weight:800; letter-spacing:-0.03em; line-height:1.1; margin-bottom:10px; background:linear-gradient(135deg, var(--tv5k-text) 0%, var(--tv5k-teal) 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
.tv5-page-kits .hero p { color:var(--tv5k-muted); font-size:14px; max-width:520px; margin:0 auto; line-height:1.5; }
.tv5-page-kits .hero-precio-ancla { display:inline-flex; align-items:baseline; gap:6px; flex-wrap:wrap; justify-content:center; margin-top:12px; font-size:13px; color:var(--tv5k-muted); }
.tv5-page-kits .hero-precio-ancla strong { font-size:22px; font-weight:900; color:var(--tv5k-text); letter-spacing:-0.02em; }

.tv5-page-kits .franja-envio { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; font-size:12px; font-weight:600; margin:20px auto 24px; max-width:800px; width:calc(100% - 40px); background:rgba(16,185,129,0.1); border:1px solid rgba(16,185,129,0.3); color:#6EE7B7; }

.tv5-page-kits .tabs-segmentos-wrap { position:sticky; top:52px; z-index:90; background:rgba(10,20,36,0.94); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border-bottom:1px solid var(--tv5k-border); margin:0 0 16px; padding:12px 0; }
.tv5-page-kits .tabs-segmentos { display:flex; gap:8px; justify-content:center; padding:0 20px; max-width:600px; margin:0 auto; overflow-x:auto; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.tv5-page-kits .tabs-segmentos::-webkit-scrollbar { display:none; }
.tv5-page-kits .tab-segmento { background:rgba(255,255,255,0.04); border:1px solid var(--tv5k-border); color:var(--tv5k-muted); padding:8px 18px; border-radius:999px; font-size:12px; font-weight:700; cursor:pointer; min-height:44px; display:inline-flex; align-items:center; transition:all 0.2s; white-space:nowrap; }
.tv5-page-kits .tab-segmento.activo { background:var(--tv5k-teal); color:var(--tv5k-navy); border-color:var(--tv5k-teal); box-shadow:0 6px 18px rgba(0,168,181,0.4); }
@media (hover:hover){ .tv5-page-kits .tab-segmento:hover { color:var(--tv5k-text); border-color:rgba(0,168,181,0.3); } }
@media (min-width:900px){ .tv5-page-kits .tabs-segmentos-wrap { top:60px; padding:14px 0; } .tv5-page-kits .tab-segmento { font-size:13px; padding:9px 22px; } }

.tv5-page-kits .card-kit-skeleton { min-height:360px; border-radius:16px; border:1px solid var(--tv5k-border); background:linear-gradient(100deg, rgba(255,255,255,0.03) 30%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 70%); background-size:200% 100%; animation:tv5kShimmer 1.3s ease infinite; }
@keyframes tv5kShimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
.tv5-page-kits .kits-vacio { text-align:center; color:var(--tv5k-muted); padding:24px 20px; font-size:14px; }
.tv5-page-kits .grid-kits { max-width:1400px; margin:0 auto; padding:0 20px; display:grid; grid-template-columns:repeat(auto-fill, minmax(260px,1fr)); gap:14px; }
@media (max-width:640px){ .tv5-page-kits .grid-kits { grid-template-columns:1fr; } }
@media (min-width:900px){ .tv5-page-kits .grid-kits { grid-template-columns:repeat(3,1fr); gap:20px; } }

.tv5-page-kits .card-kit { background:linear-gradient(135deg, rgba(0,168,181,0.08) 0%, rgba(255,255,255,0.03) 100%); border:1px solid rgba(0,168,181,0.25); border-radius:16px; padding:22px; position:relative; overflow:hidden; transition:all 0.3s; cursor:pointer; scroll-margin-top:112px; display:flex; flex-direction:column; }
.tv5-page-kits .card-kit-precio-row { margin-top:auto; }
.tv5-page-kits .card-kit:active { transform:translateY(-4px); border-color:var(--tv5k-teal); box-shadow:0 12px 32px rgba(0,168,181,0.2); }
.tv5-page-kits .card-kit.destacado { border-color:var(--tv5k-teal); box-shadow:0 0 30px rgba(0,168,181,0.15); }
.tv5-page-kits .card-kit.premium { background:linear-gradient(135deg, rgba(197,160,89,0.08) 0%, rgba(255,255,255,0.03) 100%); border-color:rgba(197,160,89,0.25); }
@media (hover:hover){ .tv5-page-kits .card-kit:hover { transform:translateY(-4px); border-color:var(--tv5k-teal); box-shadow:0 12px 32px rgba(0,168,181,0.2); } .tv5-page-kits .card-kit.premium:hover { border-color:var(--tv5k-gold); box-shadow:0 12px 32px rgba(197,160,89,0.2); } }
.tv5-page-kits .badge-mas-pedido { position:absolute; top:14px; right:14px; background:var(--tv5k-teal); color:var(--tv5k-navy); font-size:9px; font-weight:800; letter-spacing:0.08em; padding:4px 10px; border-radius:999px; text-transform:uppercase; }
.tv5-page-kits .card-kit-tier { font-size:10px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--tv5k-teal); margin-bottom:6px; }
.tv5-page-kits .card-kit.premium .card-kit-tier { color:var(--tv5k-gold); }
.tv5-page-kits .card-kit-nombre { font-size:26px; font-weight:800; letter-spacing:-0.02em; margin-bottom:8px; }
.tv5-page-kits .card-kit-para { color:var(--tv5k-muted); font-size:13px; margin-bottom:16px; line-height:1.5; }
.tv5-page-kits .card-kit-para b { color:var(--tv5k-text); }
.tv5-page-kits .card-kit-productos-lista { background:rgba(0,0,0,0.2); border-radius:10px; padding:14px; margin-bottom:12px; font-size:12px; color:var(--tv5k-muted); line-height:1.7; }
.tv5-page-kits .card-kit-productos-lista strong { color:var(--tv5k-text); font-weight:600; }
.tv5-page-kits .card-kit-productos-lista .cant { color:var(--tv5k-teal); font-weight:700; margin-right:4px; }
@media (max-width:640px){
  .tv5-page-kits .card-kit-productos-lista { max-height:128px; overflow:hidden; position:relative; transition:max-height 0.3s ease; }
  .tv5-page-kits .card-kit-productos-lista::after { content:''; position:absolute; left:0; right:0; bottom:0; height:44px; background:linear-gradient(transparent, rgba(6,12,24,0.92)); pointer-events:none; border-radius:0 0 10px 10px; }
  .tv5-page-kits .card-kit-productos-lista.expandida { max-height:720px; }
  .tv5-page-kits .card-kit-productos-lista.expandida::after { display:none; }
}
.tv5-page-kits .card-kit-ver-todos { display:none; width:100%; margin:0 0 16px; background:rgba(0,168,181,0.10); border:1px solid rgba(0,168,181,0.30); color:var(--tv5k-teal); padding:10px 14px; border-radius:10px; min-height:44px; font-size:12px; font-weight:800; cursor:pointer; align-items:center; justify-content:center; gap:6px; -webkit-tap-highlight-color:transparent; }
@media (max-width:640px){ .tv5-page-kits .card-kit-ver-todos { display:inline-flex; } }
.tv5-page-kits .card-kit-ver-todos .vt-arrow { transition:transform 0.3s; }
.tv5-page-kits .card-kit-ver-todos.expandida .vt-arrow { transform:rotate(180deg); }
.tv5-page-kits .card-kit-precio-row { display:flex; align-items:baseline; gap:8px; margin-bottom:6px; }
.tv5-page-kits .card-kit-precio { font-size:32px; font-weight:800; letter-spacing:-0.02em; }
.tv5-page-kits .card-kit-por-mes { font-size:13px; color:var(--tv5k-muted); }
.tv5-page-kits .card-kit-ahorro-linea { color:var(--tv5k-muted); font-size:12px; margin-bottom:18px; }
.tv5-page-kits .card-kit-ahorro-num { color:#6EE7B7; font-weight:700; }
.tv5-page-kits .card-kit-ahorro-tach { color:var(--tv5k-dim); text-decoration:line-through; margin-left:4px; }
.tv5-page-kits .card-kit-cta { display:block; background:linear-gradient(135deg, var(--tv5k-navy) 0%, var(--tv5k-teal) 100%); color:#fff; text-align:center; padding:14px 16px; border-radius:10px; font-weight:800; font-size:14px; text-decoration:none; transition:transform 0.2s; }
.tv5-page-kits .card-kit.premium .card-kit-cta { background:linear-gradient(135deg, var(--tv5k-navy) 0%, var(--tv5k-gold) 100%); }
.tv5-page-kits .card-kit-cta:active { transform:scale(0.97); }
@media (prefers-reduced-motion:reduce){ .tv5-page-kits .card-kit-productos-lista { transition:none; } }
`;
